import axios from "axios";
import { toast } from "react-toastify";
import i18n from "i18next";
import { ConcurrencyManager } from "axios-concurrency";
import moment from "moment";
import "moment/locale/pl";

import store from "../redux/store";
import { apiKey, apiPrefix, configAgencyName, reverseGeocodingPrefix, statuteMile } from "../config";
import { DISPATCH_PROGRESS_BAR_DATA, REVERSE_GEOCODING_DONE } from "../redux/actionTypes";
import variableColors from "../_App.module.scss";
import { exportTypeDefault } from "../data/constants";

const { darkColor, customBgColor, statusGreen, statusRed, primaryColor, statusYellow, secondaryColor, infoColor } =
  variableColors;

//* Deep copy function
export const deepCopy = (elem: Array<any> & object): Array<any> & object => {
  return JSON.parse(JSON.stringify(elem));
};

//* Format bus location
export const locationFormat = (locObj: { lat: number; lon: number }): string => {
  const lat = Number(locObj?.lat?.toFixed(4));
  const lon = Number(locObj?.lon?.toFixed(4));

  let latStr: string = "";
  let lonStr: string = "";

  if (lat >= 0) {
    latStr = `${lat}° N`;
  } else {
    latStr = `${-1 * lat}° S`;
  }
  if (lon >= 0) {
    lonStr = `${lon}° E`;
  } else {
    lonStr = `${-1 * lon}° W`;
  }
  return `${latStr}, ${lonStr}`;
};

//* Concatenate all buses
export const concatenateBuses = (busesSets: BusesSets): BusInfo[] => {
  const allBuses = Object.values(busesSets)
    .flat(1)
    .filter((elem) => typeof elem === "object");
  // console.log("allBuses:", allBuses);
  return allBuses;
};

export const addEmptyDirection = (busSet: BusInfo[]): BusInfo[] => {
  for (let i = 0; i < busSet.length; i++) {
    if (!busSet[i].direction) {
      busSet[i].direction = "---";
    }
  }
  return busSet;
};

export const truncateString = (str: string, routeNumber: string) => {
  if (str.includes("|")) {
    const splitted = str.split("|");
    const firstPart = splitted[0];
    const shortName = firstPart.replace(`${routeNumber} - `, "");
    return shortName + " ...";
  }
  return str;
};

export const truncateShiftString = (str: string | number): string => {
  const strToShort = String(str);
  if (strToShort.length >= 5) {
    const shortString = strToShort.substring(0, 5);
    return shortString + "...";
  }
  return strToShort;
};

export const getLineNumber = (str: string) => {
  if (str.includes("|")) {
    const splitted = str.split("|");
    const firstPart = splitted[0];
    return firstPart;
  } else {
    return str;
  }
};

export const getLineNumberShort = (str: string): string => {
  if (!str) {
    return null as any;
  }
  if (str.includes("-")) {
    const splitted = str.split(" - ");
    const firstPart = splitted[0];
    return firstPart;
  }
  return null as any;
};

// Todo: refactor with the function below!
//* Get Color status
export const getBusColorStatus = (bus: BusInfo): string => {
  const allowableEarlyRedux: number = store?.getState()?.constantValues?.earlyLateObj?.allowableEarlyRedux; //* [min]
  const tooFastToleration: number = allowableEarlyRedux * 60 * 1000; //* [ms]

  const color: string =
    bus?.schAdh > 10 * tooFastToleration
      ? "darkred"
      : bus?.addedStatus === "busOnTime"
        ? statusGreen
        : bus?.addedStatus === "busTooFast"
          ? statusRed
          : bus?.addedStatus === "delayedBus"
            ? statusYellow
            : bus?.addedStatus === "layoverBus"
              ? secondaryColor
              : "inherit";
  return color;
};

export const showColorStatus = (time: number): string => {
  const allowableLateRedux: number = store?.getState()?.constantValues?.earlyLateObj?.allowableLateRedux; //* [min]
  const allowableEarlyRedux: number = store?.getState()?.constantValues?.earlyLateObj?.allowableEarlyRedux; //* [min]

  const delayToleration: number = allowableLateRedux * 60 * 1000; //* [ms]
  const tooFastToleration: number = allowableEarlyRedux * 60 * 1000; //* [ms]

  let status: string = "";

  if (time >= delayToleration && time <= tooFastToleration) {
    status = "lightgreen";
  } else if (time > tooFastToleration) {
    status = "orangered";
  } else if (time < delayToleration) {
    status = "yellow";
  } else {
    status = "";
  }
  // console.log({ status });
  return status;
};

export const shapeArrayOperations = (shapeDirection: Array<Location>) => {
  let arrayDirection = [];
  for (let i = 0; i < shapeDirection.length; i++) {
    arrayDirection.push(shapeDirection[i].loc);
  }
  // console.log("arrayDirection:", arrayDirection);

  const arrayOfArrays = [] as Line[];
  for (let i = 0; i < arrayDirection.length; i++) {
    arrayOfArrays.push(arrayDirection[i]);
  }

  arrayOfArrays.sort(function (line_A, line_B) {
    return line_A.length - line_B.length;
  });

  // console.log("arrayOfArrays:", arrayOfArrays);
  return arrayOfArrays;
};

//* BusesTogetherSet sorting
export const sortedBusesTogether = (configAgencyName: string, busesTogetherSet: BusInfo[]): BusInfo[] => {
  const busesTogether: BusInfo[] = busesTogetherSet.sort((a: BusInfo, b: BusInfo) => {
    //* Sort by acronym if agency is Miami Beach
    if (configAgencyName === ("MiamiBeach" as string) && a.acronym && b.acronym) {
      return a.acronym.localeCompare(b.acronym);
    }

    //* Sort by routeShortName if agency is Doral or Miami Springs
    if (
      (configAgencyName === ("Doral" as string) ||
        configAgencyName === ("MiamiSprings" as string) ||
        configAgencyName === ("SmallCities" as string)) &&
      a.routeShortName &&
      b.routeShortName
    ) {
      return a.routeShortName.localeCompare(b.routeShortName);
    }

    if (!isNaN(Number(a.routeId)) && !isNaN(Number(b.routeId))) {
      return (
        Number(a.routeId) - Number(b.routeId) ||
        (a.vehicleName && a.vehicleName
          ? a.vehicleName.localeCompare(b.vehicleName) || Number(a.id) - Number(b.id)
          : Number(a.id) - Number(b.id))
      );
    }
    if (isNaN(Number(a?.routeId)) && isNaN(Number(b?.routeId))) {
      return (
        a?.routeId?.localeCompare(b?.routeId) ||
        (a.vehicleName && a.vehicleName
          ? a.vehicleName.localeCompare(b.vehicleName) || Number(a.id) - Number(b.id)
          : Number(a.id) - Number(b.id))
      );
    }
    return 0;
  });

  return busesTogether;
};

//* Routes list sorting
export const sortRoutes = (configAgencyName: string, routesSet: RouteDetails[]): RouteDetails[] => {
  const sortedRoutes = routesSet.sort((a: RouteDetails, b: RouteDetails) => {
    //* Sort by acronym if agency is Miami Beach
    if (configAgencyName === ("MiamiBeach" as string)) {
      return getAcronym(a?.shortName).localeCompare(getAcronym(b?.shortName));
    }

    //* Sort by routeShortName if agency is Doral or Miami Springs or Small-Cities
    if (
      configAgencyName === ("Doral" as string) ||
      configAgencyName === ("MiamiSprings" as string) ||
      configAgencyName === ("SmallCities" as string)
    ) {
      return a.shortName.localeCompare(b.shortName);
    }

    if (!isNaN(Number(a?.shortName)) && !isNaN(Number(b?.shortName))) {
      return Number(a?.shortName) - Number(b?.shortName);
    }
    if (isNaN(Number(a?.shortName)) && isNaN(Number(b?.shortName))) {
      return a?.shortName.localeCompare(b?.shortName);
    }
    return Number(a?.id) - Number(b?.id) || 0;
  });

  return sortedRoutes;
};

//* -------------------
const padTo2Digits = (num: number): string => {
  return num.toString().padStart(2, "0");
};

export const dateToString = (selectedDate: Date) => {
  //* MM-DD-YYYY
  if (!selectedDate) {
    selectedDate = new Date();
  }
  function formatDate(date: Date) {
    return [padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate()), date.getFullYear()].join("-");
  }
  const dateToExport = formatDate(selectedDate);
  // console.log("dateToExport:", dateToExport);
  return dateToExport.split("T")[0];
};

export const dateToString2 = (selectedDate: Date): string => {
  //* YYYY-MM-DD
  if (!selectedDate) {
    selectedDate = new Date();
  }
  function formatDate(date: Date) {
    return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join("-");
  }
  const dateToExport = formatDate(selectedDate);
  // console.log("dateToExport:", dateToExport);
  return dateToExport.split("T")[0];
};
//* -------------------

export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);

  const year: number = date.getFullYear();
  const month: string = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-based
  const day: string = date.getDate().toString().padStart(2, "0");
  const hours: string = date.getHours().toString().padStart(2, "0");
  const minutes: string = date.getMinutes().toString().padStart(2, "0");
  const seconds: string = date.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const timestampToString = (num: number | Date): string => {
  const date = typeof num === "number" ? new Date(num) : num;
  const strDate = date.toLocaleString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return strDate;
};

export const dateNumberStringToString = (timeDate: number | string): string => {
  const date = new Date(timeDate);
  const strDate = date.toLocaleString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return strDate;
};

// Todo: refactor TWO time functions below
export const timestampToHourMinuteString = (num: number | Date, timeZone: string): string | null => {
  if (!num || !timeZone) return null;

  const date: Date = typeof num === "number" ? new Date(num * 1000) : num;
  const strHour = date.toLocaleString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timeZone,
  });
  return strHour;
};

export const dateToTimeString = (dateObj: number | Date) => {
  // console.log({ dateObj });
  const date = typeof dateObj === "number" ? new Date(dateObj) : dateObj;
  // console.log({ date });
  const strTime = date.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });
  // console.log({ strTime });
  return strTime;
};

export const dateTimeStrToTimestamp = (str: string) => {
  str = str.includes(",") ? str.replace(",", "") : str;
  // console.log({ str });
  const [dateComponent, timeComponent] = str.split(" ");
  // console.log({ dateComponent, timeComponent });
  const [day, month, year] = dateComponent.split(".");
  const [hours, minutes, seconds] = timeComponent.split(":");
  const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds));
  // console.log({ date });
  const timestamp = date.getTime();
  // console.log({ timestamp });
  return timestamp;
};

export const toMinutesAndSeconds = (milliseconds: number): string => {
  const prefix = milliseconds >= 0 ? "" : "-";
  const totalSeconds = Number((Math.abs(milliseconds) / 1000).toFixed(0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  function padTo2Digits(num: number) {
    return num.toString().padStart(2, "0");
  }
  // console.log({ prefix, totalSeconds, minutes, seconds });
  const result = `${prefix}${padTo2Digits(minutes)}m:${padTo2Digits(seconds)}s`;
  return result;
};

// Remove seconds from time string
export const removeSecondsFromTimeString = (timeStr: string): string => {
  if (!timeStr) {
    return i18n.t("b/d");
  }

  const [hour, min] = timeStr?.split(":");
  const newTimeStr = `${hour}:${min}`;
  return newTimeStr;
};

export const showDeviationString = (str: string): string => {
  const newStr = str.replace("-", "").replace("(", "").replace(")", "").toLowerCase();
  const timeNumber = Math.round(parseFloat(str.match(/(\d+,*.*\d+)/)?.[0].replace(",", ".") || ""));
  const strArray = newStr.split(" ").slice(1);
  const finalArray = [timeNumber, ...strArray];
  const finalStr = finalArray.join(" ");
  return finalStr;
};

//* MM-DD-YYYY -> DD.MM.YYYY
export const dateConverter = (dateString: string) => {
  const dateStringModified = dateString.replaceAll("-", "/");
  const dateToString2 = Date.parse(dateStringModified);
  const dateTimeString = timestampToString(dateToString2);
  const dateTimeStringArray = dateTimeString.split(", ");
  const dateString_Modified = dateTimeStringArray[0];
  // console.log("dateString_Modified:", dateString_Modified);
  return dateString_Modified;
};

//* YYYY-MM-DD -> YYYY-MM-DD -> V3 - still testing api...
export const dateConverter2 = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  // console.log({ year, month, day });
  // const modifiedDateString = `${day}-${month}-${year}`;//* V1
  // const modifiedDateString = `${month}-${day}-${year}`; //* V2
  const modifiedDateString = `${year}-${month}-${day}`; //* V3
  // console.log("modifiedDateString:", modifiedDateString);
  return modifiedDateString;
};

//* YYYYMMDD -> YYYY-MM-DD
export const formatDateAddDashes = (dateString: string): string | null => {
  if (!dateString) {
    return null;
  }
  const year: string = dateString.substring(0, 4);
  const month: string = dateString.substring(4, 6);
  const day: string = dateString.substring(6, 8);
  // Format to YYYY-MM-DD
  return `${year}-${month}-${day}`;
};

export const calculateTimeLength = (endTime: string, startTime: string): string => {
  // console.log({ startTime, endTime });

  if (!endTime || !startTime) {
    return i18n.t("b/d");
  }

  const startTimeArray = [
    Number(startTime.split(":")[0]),
    Number(startTime.split(":")[1]),
    Number(startTime.split(":")[2]),
  ].map((elem) => Math.abs(elem));

  const endTimeArray = [Number(endTime.split(":")[0]), Number(endTime.split(":")[1]), Number(endTime.split(":")[2])].map(
    (elem) => Math.abs(elem)
  );

  const secondsStartTime = startTimeArray[0] * 3600 + startTimeArray[1] * 60 + startTimeArray[0];
  const secondsEndTime = endTimeArray[0] * 3600 + endTimeArray[1] * 60 + endTimeArray[0];

  const totalSeconds = Math.abs(secondsStartTime - secondsEndTime);
  // console.log({ totalSeconds });

  if (totalSeconds === 0) {
    return "---";
  }

  let minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const seconds = totalSeconds % 60;
  //* If seconds are greater than 30, round minutes up (optional)
  minutes = seconds >= 30 ? minutes + 1 : minutes;
  minutes = minutes % 60;

  const stringToReturn =
    hours >= 1 ? `${padTo2Digits(hours)}h : ${padTo2Digits(minutes)}min` : `${padTo2Digits(minutes)} min`;
  // console.log({ stringToReturn });
  return stringToReturn;
};

export const trimSeconds = (timeString: string) => {
  const noSecondsString = timeString.substring(0, 5);
  return noSecondsString;
};

//* Time from now
export const timeFromNow = (time: string | number, locale: string): string => {
  // console.log({ locale });
  const dateToSend = moment(time).locale(locale).fromNow();
  return dateToSend;
};

export const sortBuses = (busesArray: BusInfo[]): BusInfo[] => {
  const numArr = busesArray
    .filter((elem) => typeof elem.addedShift === "number")
    .sort(function (a, b) {
      return a.addedShift > b.addedShift ? 1 : b.addedShift > a.addedShift ? -1 : 0;
    });
  const strArr = busesArray
    .filter((elem) => typeof elem.addedShift === "string")
    .sort((a, b) => (a.addedShift > b.addedShift ? 1 : b.addedShift > a.addedShift ? -1 : 0));

  const sortedArray = numArr.concat(strArr);
  return sortedArray;
};

export const busesManipulation = (busesSet1: BusInfo[], busesSet2: Vehicle[]): BusInfo[] => {
  for (let i = 0; i < busesSet1?.length; i++) {
    for (let j = 0; j < busesSet2?.length; j++) {
      if (busesSet1[i].id === busesSet2[j].id) {
        // console.log(busesOnTime[i].id === activeBlocks[j].id);
        busesSet1[i].blockInfo = busesSet2[j].blockInfo;
      }
    }
  }
  return busesSet1;
};

//* Adding Siri info: monitored: true or false
export const addSiriData = (busesSet: BusInfo[], SiriData: VehicleSiriData[]) => {
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < SiriData?.length; j++) {
      if (busesSet[i]?.id === SiriData[j]?.id) {
        busesSet[i].monitored = SiriData[j]?.monitored;
      }
    }
  }
  return busesSet;
};

export const addColorsToBuses = (busesSet: BusInfo[], routesInfo: RouteDetails[]) => {
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < routesInfo?.length; j++) {
      const arr1 = [busesSet[i].id, busesSet[i].routeShortName];
      const arr2 = [routesInfo[j].id, routesInfo[j].shortName];
      if (arr1.some((value) => arr2.includes(value))) {
        busesSet[i].routeInfoAdded = routesInfo[j];
      }
    }
  }
  return busesSet;
};

//* Add tripLength
export const addTripPattern = (busesSet: BusInfo[], tripsPatternArray: TripPattern[]): BusInfo[] => {
  const shortShape = (shape: string): string => {
    if (!shape) return "---";
    const shapeArray: string = shape?.split("_")?.slice(0, -1)?.join("_");
    return shapeArray;
  };

  //* Standard pattern adding
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < tripsPatternArray?.length; j++) {
      if (busesSet[i].tripPattern === tripsPatternArray[j].tripPattern) {
        // console.log(busesSet[i].routeId === tripsPatternArray[j].routeId);
        // console.log(busesSet[i].direction === tripsPatternArray[j].directionId);
        busesSet[i].tripLength = tripsPatternArray[j]?.length;
        busesSet[i].currentProgress = Number(
          (100 * (busesSet[i]?.distanceAlongTrip / tripsPatternArray[j]?.length))?.toFixed(1)
        );
      }
    }
  }

  //* Check short version of pattern
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < tripsPatternArray?.length; j++) {
      if (
        busesSet[i].tripPattern !== tripsPatternArray[j].tripPattern &&
        shortShape(busesSet[i]?.tripPattern) === shortShape(tripsPatternArray[j]?.tripPattern)
      ) {
        busesSet[i].tripLength = tripsPatternArray[j]?.length;
        busesSet[i].currentProgress = Number(
          (100 * (busesSet[i]?.distanceAlongTrip / tripsPatternArray[j]?.length))?.toFixed(1)
        );
      }
    }
  }

  //* Check routeId and directionId only
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < tripsPatternArray?.length; j++) {
      if (
        !busesSet[i]?.tripLength &&
        !busesSet[i]?.currentProgress &&
        busesSet[i]?.tripPattern !== tripsPatternArray[j]?.tripPattern &&
        busesSet[i]?.routeId === tripsPatternArray[j]?.routeId &&
        busesSet[i]?.direction === tripsPatternArray[j]?.directionId
      ) {
        busesSet[i].tripLength = tripsPatternArray[j]?.length;
        busesSet[i].currentProgress = Number(
          (100 * (busesSet[i]?.distanceAlongTrip / tripsPatternArray[j]?.length))?.toFixed(1)
        );
      }
    }
  }

  return busesSet;
};

//* DynamicTextColor -> function taken from: https://wunnle.com/dynamic-text-color-based-on-background
export const dynamicTextColor = (bgcColor: string): string => {
  // console.log({ bgcColor });

  function getRGB(color: string): number {
    return parseInt(color as string, 16) || (Number(color) as number);
  }

  function getsRGB(color: string): number {
    return getRGB(color) / 255 <= 0.03928
      ? getRGB(color) / 255 / 12.92
      : Math.pow((getRGB(color) / 255 + 0.055) / 1.055, 2.4);
  }

  //* Contrast Ratio
  function getLuminance(hexColor: string): number {
    return (
      0.2126 * getsRGB(hexColor.substr(1, 2)) +
      0.7152 * getsRGB(hexColor.substr(3, 2)) +
      0.0722 * getsRGB(hexColor.substr(-2))
    );
  }

  function getContrast(colorToCheck: string, blackWhite: string): number {
    const L1 = getLuminance(colorToCheck);
    const L2 = getLuminance(blackWhite);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  }

  function getTextColor(bgcColor: string): string {
    const whiteContrast = getContrast(bgcColor, "#ffffff");
    const blackContrast = getContrast(bgcColor, "#000000");
    // console.log({ whiteContrast, blackContrast });
    return whiteContrast > blackContrast ? "#ffffff" : "#000000";
  }
  // console.log("getTextColor(bgcColor):", getTextColor(bgcColor));
  return getTextColor(bgcColor);
};

//* Invert colors -> function taken from: https://github.com/onury/invert-color
export const invertColor = (hex: string): string => {
  function padZero(str: string, len: number): string {
    len = len || 2;
    const zeros = new Array(len).join("0");
    return (zeros + str).slice(-len);
  }

  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  // Convert 3-digit hex to 6-digits
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  // Invert color components
  const r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16),
    g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16),
    b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
  // Pad each with zeros and return
  return "#" + padZero(r, 2) + padZero(g, 2) + padZero(b, 2);
};

export const dataStringToArray = (deviationTable: DeviationReport[]) => {
  for (let i = 0; i < deviationTable?.length; i++) {
    if (deviationTable[i]?.trips_late) {
      deviationTable[i].trips_late_Array = deviationTable[i].trips_late!.split("; ");
    }
    if (deviationTable[i]?.trips_early) {
      deviationTable[i].trips_early_Array = deviationTable[i].trips_early!.split("; ");
    }
  }
  return deviationTable;
};

export const valueFromLocalStorage = (localStorageItem: string): string | undefined => {
  const savedValue = localStorage.getItem(localStorageItem) as string;
  // console.log({ savedValue });
  return savedValue ? savedValue : undefined;
};

export const timeManipulation = (time: string | number, timeZone: string) => {
  const date = new Date(time);
  const localDate = date.toLocaleString("pl-PL", { timeZone: timeZone });
  return localDate;
};

export const returnRadius = (num: number): number => {
  const newRadius = Math.cbrt(Math.abs(num)) / Math.E;
  const radiusToReturn = Math.max(newRadius, 10);
  // console.log({ radiusToReturn });
  return radiusToReturn;
};

export const getTimeOnly = (str: string | null): string => {
  if (!str) {
    return i18n.t("b/d");
  }
  // console.log({ str });
  const date = new Date(str);
  // console.log({ date });
  if (date.getFullYear() > new Date().getFullYear()) {
    return i18n.t("b/d");
  }
  const time = date.toLocaleTimeString("pl-PL");
  // console.log({ time });
  return time;
};

export const deviationCalculation = (
  timeReal: string,
  timeSchedule: string
): { stringDeviation: string; colorDeviation: string; colorDeviationHex: string } => {
  if (!timeReal || !timeSchedule) {
    return { stringDeviation: i18n.t("b/d"), colorDeviation: "", colorDeviationHex: "#000" };
  }
  const time1 = Date.parse(timeReal);
  const time2 = Date.parse(timeSchedule);
  const deviation = time2 - time1;
  const deviationToReturn = deviation < 60 * 60 * 1000 ? deviation : i18n.t("b/d");
  // console.log({ deviationToReturn });

  const objectToReturn = {
    // secondsDeviation: deviation / 1000,
    stringDeviation: typeof deviationToReturn === "number" ? toMinutesAndSeconds(deviationToReturn) : i18n.t("b/d"),
    colorDeviation: typeof deviationToReturn === "number" ? showColorStatus(deviation) : "",
    colorDeviationHex: typeof deviationToReturn === "number" ? strongerColor(showColorStatus(deviation)) : "#000",
  };
  return objectToReturn;
};

function strongerColor(color: string): string {
  const color2 = color.replace("light", "");
  const color3 = color2.replace("orange", "");
  return color3;
}

export const addMinutesToDate = (date: Date, minutes: number) => {
  date.setMinutes(date.getMinutes() + minutes);
  return date;
};

//* Updated version of getting default block start/end times
export const getDefaultStartEndTimeForBlock = async (
  agencyId: string,
  blockId: string,
  currentServiceId: string[]
): Promise<StartEndBlockTime> => {
  const date = new Date();
  const dayStart = date.getDate();
  let dayEnd = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  let startEndTimeObject = {} as StartEndBlockTime;
  const URL = `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/blocks?blockId=${encodeURIComponent(blockId)}`;
  // console.log({ URL, currentServiceId });
  await axios
    .get(URL)
    .then((response) => {
      const data = response?.data?.block;
      // console.log("data:", data);

      const timesObjArray = [] as { startTime: string; endTime: string; serviceId: string }[];
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < currentServiceId.length; j++) {
          if (data[i].serviceId === currentServiceId[j]) {
            const startTime = data[i]?.startTime as string;
            const endTime = data[i]?.endTime as string;
            const serviceId = currentServiceId[j] as string;
            // console.log({ startTime, endTime, serviceId });
            const timesObj = {
              startTime,
              endTime,
              serviceId,
            };
            timesObjArray.push(timesObj);
          }
        }
      }
      // console.log("timesObjArray:", timesObjArray);
      const startTime = timesObjArray.map((time) => time?.startTime)?.sort()[0];
      const endTime = timesObjArray.map((time) => time?.endTime)?.sort()[Object?.keys(timesObjArray)?.length - 1];
      // console.log({ startTime, endTime });

      const startTimeArray = startTime.split(":");
      const startTimeHour = Number(startTimeArray[0]);
      const startTimeMinute = Number(startTimeArray[1]);
      const startTimeSecond = Number(startTimeArray[2]);
      const startDateTime = `${padTo2Digits(dayStart)}.${padTo2Digits(month)}.${padTo2Digits(year)}, ${padTo2Digits(
        startTimeHour
      )}:${padTo2Digits(startTimeMinute)}:${padTo2Digits(startTimeSecond)}`;
      // console.log({ startDateTime });

      const endTimeArray = endTime.split(":");
      let endTimeHour = Number(endTimeArray[0]);
      const endTimeMinute = Number(endTimeArray[1]);
      const endTimeSecond = Number(endTimeArray[2]);
      if (endTimeHour >= 24) {
        endTimeHour = endTimeHour - 24;
        dayEnd = dayEnd + 1;
      }
      const endDateTime = `${padTo2Digits(dayEnd)}.${padTo2Digits(month)}.${padTo2Digits(year)}, ${padTo2Digits(
        endTimeHour
      )}:${padTo2Digits(endTimeMinute)}:${padTo2Digits(endTimeSecond)}`;
      // console.log({ endDateTime });
      startEndTimeObject = { startDateTime, endDateTime };
      // console.log({ startEndTimeObject });
    })
    .catch((error) => {
      // console.log({ error });
      if (error?.message) {
        console.log("error?.message:", error?.message);
      }
    });
  return startEndTimeObject;
};

export const infoNotify = (infoType: string, message: string) => {
  if (infoType === "error" || infoType === "danger") {
    toast.error(message);
  } else if (infoType === "warning") {
    toast.warning(message, {
      style: { color: darkColor },
    });
  } else if (infoType === "success") {
    toast.success(message);
  } else if (infoType === "info") {
    toast.info(message);
  } else {
    toast(message);
  }
};

//* Reverse Geocoding - Photon Api -> Singular
export const getAddressPhoton = async (lat: number | null, lon: number | null): Promise<string> => {
  // console.log({ lat, lon });

  if (!lat || !lon) {
    return i18n.t("b/d") as string;
  }

  //* V1 -> Nominatim
  // const i18nextLng = localStorage.getItem("i18nextLng");
  // console.log({ i18nextLng });
  // const URL = `${reverseGeocodingPrefix}/reverse?lon=${lon}&lat=${lat}&format=json&accept-language=${i18nextLng}`;
  //* V2 -> Photon
  const URL = `${reverseGeocodingPrefix}/reverse?lon=${lon}&lat=${lat}&lang=en`;
  let addressResponse = "" as string;

  await fetch(URL)
    .then((res) => res.json())
    .then((data) => {
      //* V1 -> Nominatim
      // console.log(data);
      // const countryCode = data?.address?.country_code ? data?.address?.country_code.toUpperCase() : "";
      // const country = data?.address?.country ? data?.address?.country : "";
      // const state = data?.address?.state ? data?.address?.state + "," : "";
      // addressResponse = data?.display_name;
      // addressResponse = addressResponse.replace(country, countryCode);
      // addressResponse = addressResponse.replace(state, "");
      //* V2 -> Photon
      const resData = data?.features?.[0]?.properties;
      const countryCode = resData?.countrycode.toUpperCase() ?? "";
      const state = resData.state ? resData?.state : "";
      const city = resData?.city ?? "";
      const street = resData?.street ?? "";
      const name = resData?.name ? `, (${resData?.name})` : "";
      addressResponse = `${countryCode}, ${state}, ${city}, ${street} ${name}`;
    })
    .catch((error) => {
      if (error.response) {
        console.error("error.response.status:", error.response.status);
      }
      console.error("error.config:", error.config);
      return "";
    });
  return addressResponse;
};

//* Reverse Geocoding - Photon Api -> Promise.All
export const getAddressPromiseAll = async (arr: Array<any>) => {
  const api = axios.create({
    baseURL: reverseGeocodingPrefix,
  });
  const MAX_CONCURRENT_REQUESTS = 100;
  const manager = ConcurrencyManager(api, MAX_CONCURRENT_REQUESTS);

  //* Promises Status -> for progressBar!
  //* Promises Status -> for progressBar - initial dispatch
  await store.dispatch({
    type: DISPATCH_PROGRESS_BAR_DATA,
    payload: {
      progressBarData: {
        showProgress: true,
        quantity: 0,
        done: 0,
        remaining: 0,
      },
    },
  });

  const queue = manager?.queue;
  const partsQuantity = Math.ceil(arr.length / 100);
  // console.log({ queue, partsQuantity });
  for (let i = 0; i < 4 * partsQuantity; i++) {
    setTimeout(() => {
      // console.log(manager?.queue);
      // queue.length && console.log(`Done: ${arr.length - queue.length}/${arr.length}`);
      // queue.length && console.log(`Remaining: ${((queue.length / arr.length) * 100).toFixed(2)}% (${queue.length})`);
      //* Promises Status -> for progressBar - interval dispatching
      queue.length &&
        store.dispatch({
          type: DISPATCH_PROGRESS_BAR_DATA,
          payload: {
            progressBarData: {
              showProgress: true,
              quantity: arr.length,
              done: arr.length - queue.length,
              remaining: queue.length,
            },
          },
        });
    }, i * 1000);
  }

  // const i18nextLng = localStorage.getItem("i18nextLng");
  await Promise.all(
    //* V1 -> Nominatim
    // arr.map((elem) => api.get(`/reverse?lon=${elem.lon}&lat=${elem.lat}&format=json&accept-language=${i18nextLng}`))
    //* V2 -> Photon
    arr.map((elem) => api.get(`/reverse?lon=${elem.lon}&lat=${elem.lat}&lang=en`))
  )
    .then((data) => {
      // console.log("data:", data);
      for (let i = 0; i < data.length; i++) {
        //* V1 -> Nominatim
        // const countryCode = data[i].data?.address?.country_code ? data[i].data?.address?.country_code.toUpperCase() : "";
        // const country = data[i].data?.address?.country ? data[i].data?.address?.country : "";
        // const state = data[i].data?.address?.state ? data[i].data?.address?.state + "," : "";
        // let addressResponse = data[i].data?.display_name;
        // addressResponse = addressResponse.replace(country, countryCode);
        // addressResponse = addressResponse.replace(state, "");
        // arr[i].address = addressResponse;
        //* V2 -> Photon
        const resData = data[i]?.data?.features?.[0]?.properties;
        const countryCode = resData?.countrycode.toUpperCase() ?? "";
        const state = resData.state ? resData?.state : "";
        const city = resData?.city ?? "";
        const street = resData?.street ?? "";
        const name = resData?.name ? `, (${resData?.name})` : "";
        arr[i].address = `${countryCode}, ${state}, ${city}, ${street} ${name}`;
      }
    })
    .catch((error) => {
      if (error.response) {
        console.error("error.response.status:", error.response.status);
      }
      console.error("error.config:", error.config);
    });
  await manager.detach();

  //* Promises Status -> for progressBar - dispatch 100% done
  await store.dispatch({
    type: DISPATCH_PROGRESS_BAR_DATA,
    payload: {
      progressBarData: {
        showProgress: true,
        quantity: arr.length,
        done: arr.length,
        remaining: queue.length,
      },
    },
  });

  await setTimeout(() => {
    store.dispatch({ type: REVERSE_GEOCODING_DONE, payload: { geocodingDataLoading: false } });
    //* Promises Status -> for progressBar - final dispatch
    store.dispatch({
      type: DISPATCH_PROGRESS_BAR_DATA,
      payload: {
        progressBarData: {
          showProgress: false,
          quantity: 0,
          done: 0,
          remaining: 0,
        },
      },
    });
  }, 2000);
  return arr;
};

//* String translation
export const translateString = (str: string): string => {
  if (!str) return i18n.t("b/d");

  let stringToReturn: string;

  switch (true) {
    case str?.includes("na czas"):
      stringToReturn = str?.replace("na czas", `${i18n.t("Na czas")}`);
      break;
    case str?.includes("on time"):
      stringToReturn = str?.replace("on time", `${i18n.t("Na czas")}`);
      break;
    case str?.includes("wcześniej"):
      stringToReturn = str?.replace("wcześniej", `${i18n.t("Wcześniej")}`);
      break;
    case str?.includes("early"):
      stringToReturn = str?.replace("early", `${i18n.t("Wcześniej")}`);
      break;
    case str?.includes("później"):
      stringToReturn = str?.replace("później", `${i18n.t("Później")}`);
      break;
    case str?.includes("late"):
      stringToReturn = str?.replace("late", `${i18n.t("Później")}`);
      break;
    default:
      stringToReturn = str;
  }
  return stringToReturn;
};

//* Random HSL Color Generator
export const random_HSL_ColorGenerator = (busesNumber: number): string[] => {
  // console.log({ busesNumber });

  // Function taken from here: https://css-tricks.com/converting-color-spaces-in-javascript/#aa-hex-to-hsl
  const HSLToHexConverter = (str: string): string => {
    // console.log({ str });

    const strArray = str.split(",");
    const hue = Number(strArray[0]);
    const saturation = Number(strArray[1]) / 100;
    const lightness = Number(strArray[2]) / 100;

    // Comma operator, HSL to RGB first
    let chroma = ((1 - Math.abs(2 * lightness - 1)) * saturation) as number,
      x = (chroma * (1 - Math.abs(((hue / 60) % 2) - 1))) as number,
      m = (lightness - chroma / 2) as number,
      red = 0 as number,
      green = 0 as number,
      blue = 0 as number;
    // console.log({ chroma, x, m });

    if (0 <= hue && hue < 60) {
      red = chroma;
      green = x;
      blue = 0;
    } else if (60 <= hue && hue < 120) {
      red = x;
      green = chroma;
      blue = 0;
    } else if (120 <= hue && hue < 180) {
      red = 0;
      green = chroma;
      blue = x;
    } else if (180 <= hue && hue < 240) {
      red = 0;
      green = x;
      blue = chroma;
    } else if (240 <= hue && hue < 300) {
      red = x;
      green = 0;
      blue = chroma;
    } else if (300 <= hue && hue < 360) {
      red = chroma;
      green = 0;
      blue = x;
    }
    // console.log({ red, green, blue });

    // Having obtained RGB, convert channels to HEX
    let redString: string = Math.round((red + m) * 255).toString(16);
    let greenString: string = Math.round((green + m) * 255).toString(16);
    let blueString: string = Math.round((blue + m) * 255).toString(16);

    // Prepend 0s, if necessary
    if (redString.length === 1) {
      redString = "0" + redString;
    }
    if (greenString.length === 1) {
      greenString = "0" + greenString;
    }
    if (blueString.length === 1) {
      blueString = "0" + blueString;
    }
    return "#" + redString + greenString + blueString;
  };

  // minNumber and maxNumber are included
  const randomNumberGeneratorFromRange = (minNumber: number, maxNumber: number): number => {
    const randomNumber = Math.floor(Math.random() * (maxNumber - minNumber + 1) + minNumber);
    // console.log({ randomNumber });
    return randomNumber;
  };

  const HSL_colorGenerator = (num: number): string[] => {
    // console.log({ num });
    //* HSL values
    const hueInterval = 360 / num;
    const hueArray = [] as number[];
    for (let i = 0; i < num; i++) {
      hueArray.push(i * hueInterval);
    }
    // console.log("hueArray:", hueArray);

    const HSL_Colors: string[] = hueArray.map(
      (elem) => `${Math.round(elem)},${randomNumberGeneratorFromRange(40, 100)},${randomNumberGeneratorFromRange(40, 80)}`
    );
    // console.log("HSL_Colors:", HSL_Colors);

    const HEX_Colors: string[] = HSL_Colors.map((elem) => HSLToHexConverter(elem));
    // console.log("HEX_Colors:", HEX_Colors);
    return HEX_Colors;
  };

  const generatedColors: string[] = HSL_colorGenerator(busesNumber);
  // console.log("generatedColors:", generatedColors);
  return generatedColors;
};

// Todo: Refactor functions that remove underscores
//* Remove underscores [_] in array of objects e.g. vehicle_id -> vehicleid (For TC2.0)
export const removeUnderscores = (objArray: VehicleReportAVL[], oldKey: string, newKey: string): VehicleReportAVL[] => {
  for (let i = 0; i < objArray.length; i++) {
    if (!objArray[i]?.[newKey as keyof VehicleReportAVL]) {
      (objArray as any)[i][newKey] = objArray[i]?.[oldKey as keyof VehicleReportAVL];
      delete objArray[i]?.[oldKey as keyof VehicleReportAVL];
    }
  }
  return objArray;
};

//* Remove all underscores [_] from key names in array of objects e.g. vehicle_id -> vehicleid (For TC2.0)
export const removeAllUnderscores = <T extends VehicleReportAVL>(arr: T[]): T[] => {
  const newArr = arr.map((obj: T) => {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.replace(/_/g, ""), value]));
  }) as T[];
  return newArr;
};

//* ConcurrencyManager - Get All Vehicles AVL Data
export const getAllVehiclesConcurrencyAVLData = async (
  agencyId: string,
  arr: string[],
  fetchDate: string,
  numDays: number
): Promise<{ allVehiclesDataFiltered: AllVehiclesReportAVL[]; fetchingDone: boolean }> => {
  const api = axios.create({
    baseURL: `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/reports`,
  });
  const MAX_CONCURRENT_REQUESTS = 2;
  const manager = ConcurrencyManager(api, MAX_CONCURRENT_REQUESTS);
  const allVehiclesData: AllVehiclesReportAVL[] = [];

  await Promise.all(arr.map((elem: string) => api.get(`/avlReport?v=${elem}&beginDate=${fetchDate}&numDays=${numDays}`)))
    .then((data) => {
      // console.log("data:", data);
      for (let i = 0; i < data.length; i++) {
        if (data[i].status === 200) {
          // console.log("data[i]?.data.data:", data[i]?.data.data);
          let positionsArray = data[i]?.data?.data as VehicleReportAVL[];
          // console.log("positionsArray[0]:", positionsArray[0]);
          positionsArray = removeUnderscores(positionsArray, "vehicle_id", "vehicleid");
          positionsArray = removeUnderscores(positionsArray, "time_processed", "timeprocessed");
          // console.log("positionsArray[0]:", positionsArray[0]);

          const positionsArrayFiltered = positionsArray.filter((obj, number: number) => {
            return (
              positionsArray.findIndex((elem) => {
                return elem.lat === obj.lat && elem.lon === obj.lon;
              }) === number
            );
          });

          const busDataAVL = {
            busId:
              ([...new Set(data[i]?.data?.data.map((busData: { vehicleid: string }) => busData.vehicleid))][0] as string) ||
              "",
            positions: positionsArrayFiltered as VehicleReportAVL[],
          };
          allVehiclesData.push(busDataAVL);
        }
      }
      // console.log("allVehiclesData:", allVehiclesData);
    })
    .catch((error) => {
      if (error.response) {
        console.error("error.response.status:", error.response.status);
      }
      console.error("error.config:", error.config);
    });
  await manager.detach();
  const allVehiclesDataFiltered = await allVehiclesData.filter((item) => item.busId && item.positions.length);

  for (let i = 0; i < allVehiclesDataFiltered.length; i++) {
    allVehiclesDataFiltered[i].itemsNumber = allVehiclesDataFiltered[i].positions.length;
    for (let k = 0; k < allVehiclesDataFiltered[i].positions.length; k++) {
      allVehiclesDataFiltered[i].positions[k].itemNo = `${k + 1}/${allVehiclesDataFiltered[i].positions.length}`;
    }
  }
  // console.log("allVehiclesDataFiltered:", allVehiclesDataFiltered);
  return await { allVehiclesDataFiltered: allVehiclesDataFiltered, fetchingDone: true };
};

//* Remove duplicates from array
export const removeDuplicates = (array: Array<any>, property: string): Array<any> => {
  const uniqueIds = [] as typeof array;
  const unique = array.filter((element) => {
    const isDuplicate = uniqueIds.includes(element[property]);
    if (!isDuplicate) {
      uniqueIds.push(element[property]);
      return true;
    }
    return false;
  });
  return unique;
};

export const changeTimePeriodFormat = (date1: string, date2: string) => {
  const time1 = date1.split(" ")[1];
  const time2 = date2.split(" ")[1];
  const timePeriod = `${time1.replace(".000", "")} ${String.fromCharCode(8212)} ${time2.replace(".000", "")}`;
  return timePeriod;
};

//* Capitalize first letter
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) {
    return "";
  }
  const capitalizedStr = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  return capitalizedStr;
};

//* Return trip status
export const getTripStatus = (bus: BusInfo): string => {
  return bus?.cancelReenableData?.scheduleRelationship === "SCHEDULED"
    ? "SCHEDULED"
    : bus?.cancelReenableData?.scheduleRelationship === "CANCELED"
      ? "CANCELED"
      : "Wyłączony";
};

//* Modify calendar object
export const calendarObjectModify = (obj: Object): string => {
  const workingDays = [] as string[];
  for (const [key, value] of Object.entries(obj)) {
    if (value === true) {
      workingDays.push(key);
    }
  }

  const workingDaysModified = workingDays.map((day: string) => capitalizeFirstLetter(day));
  const workingDaysShort = workingDaysModified.map((day: string) => day.substring(0, 3));
  const workingDaysString = workingDaysShort.join("-");
  return workingDaysString;
};

//* Remove pksxxx form buses names - string
export const removePKSXXX_string = (name: string): string => {
  const newName: string = name.replace(" ", "").replace(")", "").split("(")?.[0];
  return newName;
};

//* Remove pksxxx form buses names - object
export const removePKSXXX = (vehicleObjectsArray: Array<any>, keyName: string): Array<any> => {
  for (let i = 0; i < vehicleObjectsArray.length; i++) {
    if (vehicleObjectsArray[i][keyName]) {
      const strArray = vehicleObjectsArray[i][keyName].replace(" ", "").replace(")", "").split("(");
      vehicleObjectsArray[i].pksName = strArray[1] || "";
      vehicleObjectsArray[i][keyName] = strArray[0];
    }
  }
  return vehicleObjectsArray;
};

//* Subcontractors PKS checking
export const checkSubcontractorPKS = async <T extends BusInfo | VehicleReportAVL>(
  vehiclesArray: T[],
  propertyName: string
): Promise<T[]> => {
  const subcontractorsPKS = (await window?.config?.subcontractorsPKS) as string[];
  // console.log("subcontractorsPKS:", subcontractorsPKS);

  const trimmedSubcontractorsPKS = subcontractorsPKS?.map((elem: string) => elem?.trim());
  // console.log("trimmedSubcontractorsPKS:", trimmedSubcontractorsPKS);

  for (let i = 0; i < vehiclesArray.length; i++) {
    if (trimmedSubcontractorsPKS.includes((vehiclesArray[i] as any)[propertyName])) {
      vehiclesArray[i].subcontractorPKS = true;
    }
    if (!trimmedSubcontractorsPKS.includes((vehiclesArray[i] as any)[propertyName])) {
      vehiclesArray[i].subcontractorPKS = false;
    }
  }

  return vehiclesArray;
};

//* getAcronym
export const getAcronym = (words: string): string => {
  const acronym = words
    ?.split(" ")
    ?.map((word: string) => word?.charAt(0)?.toUpperCase())
    ?.join("");
  return acronym;
};

//* Remove duplicates of coordinates
export const removeCoordinatesDuplicates = <T extends Coordinate>(dataSet: T[], addFirstLast: boolean): T[] => {
  const firstLastElem = [dataSet?.[0], dataSet[dataSet?.length - 1]];
  // console.log("firstLastElem:", firstLastElem);

  const dataSetFiltered: T[] = [...dataSet]?.filter((obj: T, index: number) => {
    return (
      dataSet.findIndex((elem: T) => {
        return elem.lat === obj.lat && elem.lon === obj.lon;
      }) === index
    );
  });

  //* Add first and last elem
  const dataSetFilteredLonger = [firstLastElem?.[0], ...dataSetFiltered, firstLastElem?.[1]];
  const dataToReturn = addFirstLast ? dataSetFilteredLonger : dataSetFiltered;
  return dataToReturn;
};

//* Remove seconds from timeString
export const removeSeconds = (timeString: string, dayHours: number = 24): string => {
  if (!timeString) return "---";

  let newTime: string = "";
  if (Number(timeString.split(":")[0]) >= dayHours) {
    let [hours, minutes, seconds] = timeString.split(":").map((elem) => Number(elem));
    hours = hours % dayHours;
    newTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  } else {
    newTime = timeString;
  }

  const timeArray = newTime.split(":");
  const shortTime = `${timeArray[0]}:${timeArray[1]}`;
  return shortTime;
};

//* Remove seconds from timeString
export const showNextDayBadge = (timeString: string, dayHours: number = 24): string | null => {
  if (!timeString) return null;

  const numberOfHours = Number(timeString.split(":")[0]);
  const numberOfDays = Math.floor(numberOfHours / dayHours);
  return numberOfDays >= 1 ? `+${numberOfDays}` : null;
};

//* Calculate time: add/subtract time in ms
export const calculateNewTime = (timeFromApi: string, deviation: number, showSeconds?: boolean): string => {
  let newTimeFromApi: string = "";
  if (Number(timeFromApi.split(":")[0]) >= 24) {
    let [hours, minutes, seconds] = timeFromApi.split(":").map((elem) => Number(elem));
    hours = hours - 24;
    newTimeFromApi = `${hours}:${minutes}:${seconds}`;
  } else {
    newTimeFromApi = timeFromApi;
  }
  const parsedTime = moment(newTimeFromApi, "HH:mm:ss");
  // console.log('parsedTime.format("HH:mm"):', parsedTime.format("HH:mm"));

  const deviationTime =
    showSeconds === true
      ? moment(parsedTime).add(-deviation, "ms").format("HH:mm:ss")
      : moment(parsedTime).add(-deviation, "ms").format("HH:mm");
  // console.log("deviationTime:", deviationTime);
  return deviationTime;
};

//* Calculate new time do departure/arrival;
export const calculateNewTimeDifference = (timeFromApi: string, deviation: number, timeZone: string): string => {
  if (!timeZone) return "";

  // console.log("timeFromApi, deviation:", timeFromApi, deviation);
  const newTime = calculateNewTime(timeFromApi, deviation, true);
  // console.log("newTime:", newTime);
  const now = new Date().toLocaleTimeString("pl-PL", { timeZone: timeZone });
  // console.log("now:", now);
  const timeDifference = calculateTimeLength(newTime, now);
  // console.log("timeDifference:", timeDifference);
  const stringToReturn = timeDifference === "---" ? i18n.t("Teraz") : `${i18n.t("za")} ${timeDifference}`;
  // console.log("stringToReturn:", stringToReturn);
  return stringToReturn;
};

//* Add cancel/reenable trip data
export const addCancelReenableData = (busesSet: BusInfo[], cancelReenableData: CancelReenableTrip[]): BusInfo[] => {
  for (let i = 0; i < busesSet?.length; i++) {
    for (let j = 0; j < cancelReenableData?.length; j++) {
      if (busesSet[i]?.trip === cancelReenableData[j]?.tripId) {
        busesSet[i].cancelReenableData = cancelReenableData[j];
      }
    }
  }
  return busesSet;
};

//* Bus progress by bus
export const tripProgress = (currentBus: BusInfo, speedUnit: string): string => {
  if (currentBus?.currentProgress || currentBus?.currentProgress === 0) {
    const kmsString: string = `${currentBus?.currentProgress}%  ( ${(0.001 * currentBus?.distanceAlongTrip).toFixed(1)} / ${(
      0.001 * currentBus?.tripLength
    ).toFixed(1)} km )`;

    const milesString: string = `${currentBus?.currentProgress}%  ( ${(
      (0.001 * currentBus?.distanceAlongTrip) /
      statuteMile
    ).toFixed(1)} / ${((0.001 * currentBus?.tripLength) / statuteMile).toFixed(1)} miles )`;

    return speedUnit === "kmh" ? kmsString : speedUnit === "mph" ? milesString : i18n.t("b/d");
  } else {
    return i18n.t("b/d");
  }
};

//* Transform Prediction Accuracy Interval data into 2-D array
export const transformDataTo2DArray = (data: AccuracyIntervalData[]): number[][] => {
  // Initialize an array for each column
  const dataArray2D: number[][] = Array?.from({ length: data?.[0]?.c?.length }, () => []);

  // Populate each column array with the corresponding values
  data.forEach((entry) => {
    entry?.c?.forEach((value, index) => {
      dataArray2D[index].push(value?.v as number);
    });
  });
  // Return the 2D array
  return dataArray2D;
};

//* Min-max value
export const setMinMaxValue = (minValue: number, maxValue: number, value: string): number => {
  const valueToReturn = Math.max(minValue, Math.min(maxValue, Number(value)));
  return valueToReturn;
};

//@ Removing null values properties from array of objects
type RemoveNull = Alert | BusInfo | ObjectI;
export function filterArrayNullProperties<T extends RemoveNull>(inputArray: T[]): T[] {
  if (!Array.isArray(inputArray)) {
    return null as any; // Return null for non-array values
  }
  return inputArray?.map((inputObj: T) => filterNullProperties(inputObj));
}

function filterNullProperties<T extends RemoveNull>(inputObj: T): T {
  if (inputObj === null || typeof inputObj !== "object") {
    return inputObj; // Return as is for non-object and null values
  }
  //* Output Object
  const outputObj: T = {} as T;

  for (const key in inputObj) {
    if (inputObj[key] !== null) {
      if (Array.isArray(inputObj[key])) {
        const filteredArray = filterArrayNullProperties(inputObj[key] as T[]);
        if (filteredArray !== null && filteredArray?.length > 0) {
          (outputObj as any)[key] = filteredArray;
        }
      } else if (typeof inputObj[key] === "object") {
        const filteredNestedObj = filterNullProperties(inputObj[key] as T);
        if (filteredNestedObj !== null && Object.keys(filteredNestedObj)?.length > 0) {
          (outputObj as any)[key] = filteredNestedObj;
        }
      } else {
        outputObj[key] = inputObj[key];
      }
    }
  }
  return Object.keys(outputObj)?.length > 0 ? outputObj : (null as any);
}

//^ Service Alert Manager
//* Date Object to "YYYY-MM-DD HH:mm" format converter
export const getDateTimeString = (dateTime: Date): string => {
  const date = dateTime
    .toLocaleDateString("pl", { year: "numeric", month: "2-digit", day: "2-digit" })
    .split(".")
    .reverse()
    .join("-");
  const time = dateTime.toLocaleTimeString("pl", { hour: "2-digit", minute: "2-digit" });
  const strDateTime = `${date} ${time}`;
  return strDateTime;
};

//* Remove Null and Undefined form Array
export const arrayRemoveNullUndefined = (array: Array<any>): (string | number)[] => {
  return array.filter((elem) => elem !== undefined && elem !== null);
};

//* DeepEqualCheck comparison
export const deepEqualCheck = (obj1: object | Array<object>, obj2: object | Array<object>): boolean => {
  // Check if both arguments are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    // Check if arrays have same length
    if (obj1.length !== obj2.length) {
      return false;
    }

    // Iterate through array elements
    for (let i = 0; i < obj1.length; i++) {
      // Recursively compare array elements
      if (!deepEqualCheck(obj1[i], obj2[i])) {
        return false;
      }
    }

    // All array elements are equal
    return true;
  }

  // Check if both arguments are objects
  if (typeof obj1 === "object" && typeof obj2 === "object" && obj1 !== null && obj2 !== null) {
    // Get keys of both objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if number of keys are same
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Iterate through keys of first object
    for (const key of keys1) {
      // Check if key exists in second object
      if (!keys2.includes(key)) {
        return false;
      }

      // Recursively compare nested properties
      if (!deepEqualCheck(obj1[key as keyof object], obj2[key as keyof object])) {
        return false;
      }
    }

    // All properties and nested properties are equal
    return true;
  }

  // Compare primitive values directly
  return obj1 === obj2;
};

//* Calculate time range within day
export const calculateTimeRangeWithinDay = (
  inputDate: Date,
  rangeInMinutes: number
): { timeMinusRange: string; timePlusRange: string } => {
  if (isNaN(inputDate.getTime())) {
    throw new Error("Invalid time format. Please use 'YYYY-MM-DD HH:mm'.");
  }

  // Extract date components
  const year = inputDate.getFullYear();
  const month = String(inputDate.getMonth() + 1).padStart(2, "0");
  const day = String(inputDate.getDate()).padStart(2, "0");

  // Calculate minutes since start of day
  const minutesSinceMidnight: number = inputDate.getHours() * 60 + inputDate.getMinutes();
  // console.log({minutesSinceMidnight})

  // Calculate new minutes, clamping to 0-1440 range
  const minusMinutes: number = Math.max(0, minutesSinceMidnight - rangeInMinutes);
  const plusMinutes: number = Math.min(1440, minutesSinceMidnight + rangeInMinutes);

  // Format the times
  const formatTime = (minutes: number): string => {
    if (minutes === 0) return "00:00";
    if (minutes === 1440) return "24:00";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  };

  return {
    timeMinusRange: `${year}-${month}-${day} ${formatTime(minusMinutes)}`,
    timePlusRange: `${year}-${month}-${day} ${formatTime(plusMinutes)}`,
  };
};

//* Closest time to selectedTime in BusLocationByDate.tsx
export const findClosestTimeToSelectedTime = (arr: VehicleReportAVL[], timeString: string): VehicleReportAVL | null => {
  if (arr.length === 0) return null;

  // Convert timeString to minutes since midnight
  const [hours, minutes] = timeString.split(":").map(Number);
  const targetMinutes = hours * 60 + minutes;

  return arr.reduce(
    (closest, current) => {
      // Extract time from the current item's time string
      const currentTime = current.time.split(" ")[1].split(":");
      const currentHours = parseInt(currentTime[0]);
      const currentMinutes = parseInt(currentTime[1]);
      const currentTotalMinutes = currentHours * 60 + currentMinutes;

      // Calculate the difference in minutes
      const currentDiff = Math.abs(currentTotalMinutes - targetMinutes);

      // If this is the first item or it's closer than the previous closest, update closest
      if (!closest || currentDiff < closest.diff) {
        return { item: current, diff: currentDiff };
      }
      return closest;
    },
    { item: null as VehicleReportAVL | null, diff: Infinity }
  ).item;
};

//* Mix two colors
export const mixColors = (hex1: string, hex2: string, percentage: number = 50): string => {
  // Check if hex values are valid
  if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex1) || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex2)) {
    throw new Error("Invalid hex color format");
  }

  // Normalize hex values to 6 digits
  hex1 = hex1.replace("#", "") as string;
  hex2 = hex2.replace("#", "") as string;
  if (hex1.length === 3) hex1 = hex1[0] + hex1[0] + hex1[1] + hex1[1] + hex1[2] + hex1[2];
  if (hex2.length === 3) hex2 = hex2[0] + hex2[0] + hex2[1] + hex2[1] + hex2[2] + hex2[2];

  // Convert hex to RGB
  const r1: number = parseInt(hex1.substring(0, 2), 16);
  const g1: number = parseInt(hex1.substring(2, 4), 16);
  const b1: number = parseInt(hex1.substring(4, 6), 16);
  const r2: number = parseInt(hex2.substring(0, 2), 16);
  const g2: number = parseInt(hex2.substring(2, 4), 16);
  const b2: number = parseInt(hex2.substring(4, 6), 16);

  // Mix colors
  const r: number = Math.round(r1 + ((r2 - r1) * percentage) / 100);
  const g: number = Math.round(g1 + ((g2 - g1) * percentage) / 100);
  const b: number = Math.round(b1 + ((b2 - b1) * percentage) / 100);

  // Convert RGB back to hex
  const rHex: string = r.toString(16).padStart(2, "0");
  const gHex: string = g.toString(16).padStart(2, "0");
  const bHex: string = b.toString(16).padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
};

//* Get base URL for stats charts
export const getBaseStatsUrl = (apiPrefixWeb: string, apiPrefix: string, apiT33component: string): string => {
  //Todo: Adjust endpoints to TC3.0?
  const baseStatsUrl: string = apiPrefixWeb ? apiPrefixWeb : apiPrefix.replace(/\/api$/, "");
  // console.log({ baseStatsUrl });
  const finalBaseStatsUrl = !apiT33component ? baseStatsUrl : `${baseStatsUrl}/${apiT33component}`;
  // console.log({ finalBaseStatsUrl });
  return finalBaseStatsUrl;
};

//* Prediction Accuracy Range Summary
export const predictionAccuracyRangeSummary = (chartPointsSets: number[][]): string[] => {
  // console.log("chartPointsSets:", chartPointsSets);

  const chartPointsSetsSums: number[] = chartPointsSets?.map((dataSet: number[]) =>
    dataSet?.reduce((a: number, b: number) => a + b, 0)
  );
  // console.log("chartPointsSetsSums:", chartPointsSetsSums);

  const chartPointsSetsTotalSum: number = chartPointsSetsSums?.reduce((a: number, b: number) => a + b, 0);
  // console.log("chartPointsSetsTotalSum:", chartPointsSetsTotalSum);

  const chartPointsSetsPercentages: string[] = chartPointsSetsSums?.map(
    (setSum: number) => `${Number(((setSum / chartPointsSetsTotalSum) * 100).toFixed(2))}`
  );
  // console.log("chartPointsSetsPercentages:", chartPointsSetsPercentages);

  //Todo: Is it correct? String(chartPointsSetsTotalSum) - sum very...big!
  const summaryToReturn: string[] = [String(chartPointsSetsTotalSum), ...chartPointsSetsPercentages];
  // console.log("summaryToReturn:", summaryToReturn);
  return summaryToReturn;
};

//* VehicleName or VehicleId
export const vehicleNameOrId = (historicalPosition: VehicleReportAVL): string => {
  let nameToReturn: string =
    historicalPosition?.name && historicalPosition?.vehicleid && historicalPosition?.name !== historicalPosition?.vehicleid
      ? `${historicalPosition?.name} (${historicalPosition?.vehicleid})`
      : historicalPosition?.name || historicalPosition?.vehicleid || i18n.t("b/d");

  if (configAgencyName === ("PoznanPKS" as string)) {
    nameToReturn = nameToReturn.split("(")?.[0];
  }

  return nameToReturn;
};

//* Select most frequent routeType
export const mostFrequent = (arr: string[]): string => {
  let count = {} as { [key: string]: number };
  let maxCount = 0;
  let result = null as unknown as string;

  for (let str of arr) {
    count[str] = (count[str] || 0) + 1; // Increment count for each string
    if (count[str] > maxCount) {
      maxCount = count[str]; // Update maxCount
      result = str; // Update result with the current string
    }
  }
  return result; // Return the most frequent string
};

//* Calculate occurrences by busId in single trip report
export const calculateOccurrences = (arr: string[]): { busId: string; number: number }[] => {
  const arrUnique: string[] = [...new Set(arr)];

  const occurrences: { busId: string; number: number }[] = arrUnique
    .map((bus) => {
      return {
        busId: bus,
        number: arr?.filter((elem: string) => elem === bus)?.length,
      };
    })
    .sort((a: { busId: string; number: number }, b: { busId: string; number: number }) => b.number - a.number);

  return occurrences;
};

//* Convert scheduleRelationship from numeric to string value
export const getScheduleRelationshipString = (value: number): string => {
  const scheduleRelationshipMap = {
    0: "SCHEDULED",
    1: "ADDED",
    2: "UNSCHEDULED",
    3: "CANCELED",
    4: "DUPLICATED",
    5: "DELETED",
  } as { [key: number]: string };

  return scheduleRelationshipMap[value as keyof typeof scheduleRelationshipMap] || "UNKNOWN";
};

//* Convert occupancyStatus from numeric to string value
export const getOccupancyStatusString = (value: number): string => {
  const occupancyStatusMap = {
    0: "EMPTY",
    1: "MANY_SEATS_AVAILABLE",
    2: "FEW_SEATS_AVAILABLE",
    3: "STANDING_ROOM_ONLY",
    4: "CRUSHED_STANDING_ROOM_ONLY",
    5: "FULL",
    6: "NOT_ACCEPTING_PASSENGERS",
    7: "NO_DATA_AVAILABLE",
    8: "NOT_BOARDABLE",
  } as { [key: number]: string };

  return occupancyStatusMap[value as keyof typeof occupancyStatusMap] || "UNKNOWN";
};

//* Format to HH:mm with rounding
export const formatDateToTimeStringWithRounding = (dateString: string): string | null => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);

  // Extract hours, minutes, and seconds
  let hours: number = date.getHours();
  let minutes: number = date.getMinutes();
  const seconds: number = date.getSeconds();

  // Round up minutes if seconds are 30 or more
  if (seconds >= 30) {
    minutes += 1;
  }

  // Handle minute overflow (if minutes go to 60)
  if (minutes === 60) {
    minutes = 0;
    hours += 1;

    // Handle hour overflow (if hours go to 24)
    if (hours === 24) {
      hours = 0; // Reset hours if it goes to 24
    }
  }

  // Format hours and minutes to ensure two digits
  const hoursStr = String(hours).padStart(2, "0");
  const minutesStr = String(minutes).padStart(2, "0");
  return `${hoursStr}:${minutesStr}`;
};

//* Check if admin's email
export const isAdminEmail = (email: string): boolean => {
  const auth0AdminEmails: string[] = (window?.config?.auth0AdminEmails || []) as string[];
  const isAdmin: boolean = auth0AdminEmails.includes(email);
  return isAdmin;
};

//* Get number of icons
export const getIconsNumber = (occupancyStatus: string): number => {
  const occupancyStatusMap = {
    EMPTY: 1,
    MANY_SEATS_AVAILABLE: 1,
    FEW_SEATS_AVAILABLE: 2,
    STANDING_ROOM_ONLY: 2,
    CRUSHED_STANDING_ROOM_ONLY: 3,
    FULL: 3,
  };

  const occupancyStatusValue: number = occupancyStatusMap[occupancyStatus as keyof typeof occupancyStatusMap] || 0;
  return occupancyStatusValue;
};

//* Get downloadable stops data - helper function
export const addStringStatusToDownloadableContent = (downloadableContent: DownloadableContent[]): DownloadableContent[] => {
  const statusMap: { [key: number | string]: string } = {
    "-1": "Error",
    1: "New",
    2: "Processing",
    3: "Ready",
  };

  return downloadableContent?.map((item: DownloadableContent) => {
    const addedStatus: string = statusMap[item?.exportStatus] || ""; // Use the map, default to ""
    return { ...item, addedStatus };
  });
};

//* Get downloadable stops data
// Todo: refactor with getDownloadableTA_content
export const getDownloadableStopsData = async (agencyId: string, type: number): Promise<DownloadableContent[]> => {
  try {
    const { data } = await axios.get(
      `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/exports?type=${type ?? exportTypeDefault}`
    );
    // console.log("data:", data);
    const downloadableStopsContent = ((data?.exports || data?.exportsData) as DownloadableContent[])
      //* Filter data: return data now + one day - Dates are timestamps!
      ?.filter((elem: DownloadableContent) => elem?.dataDate <= Date.now() + 24 * 60 * 60 * 1000)
      //* Sort by dataDate ascending and then by exportDate ascending - Dates are timestamps!
      ?.sort((a: DownloadableContent, b: DownloadableContent) => b?.exportDate - a?.exportDate || b?.dataDate - a?.dataDate);
    // console.log("downloadableStopsContent:", downloadableStopsContent);

    const downloadableStopsContentModified: DownloadableContent[] =
      addStringStatusToDownloadableContent(downloadableStopsContent);
    // console.log("downloadableStopsContentModified:", downloadableStopsContentModified);

    const downloadableStopsContentFiltered: DownloadableContent[] = removeDuplicates(
      downloadableStopsContentModified,
      "file"
    );
    // console.log("downloadableStopsContentFiltered:", downloadableStopsContentFiltered);

    return downloadableStopsContentFiltered;
  } catch (error) {
    if ((error as CustomError).response) {
      console.error("error.response.status:", ((error as CustomError).response as { status: number }).status);
    }
    console.error("error.config:", (error as CustomError).config);
    return [] as DownloadableContent[];
  }
};

//* Add startDate and endDate
export const addStartDateEndDate = (stopsData: DownloadableContent[]): DownloadableContent[] => {
  const modifiedArray = stopsData?.map((file: DownloadableContent): DownloadableContent => {
    const { fileName }: { fileName: `${string}.csv` } = file;

    // Use regex to extract dates: (\d{4}-\d{2}-\d{2}) captures a date in YYYY-MM-DD format.
    const dateMatch: RegExpMatchArray | null = fileName?.match(/(\d{4}-\d{2}-\d{2})[-_](\d{4}-\d{2}-\d{2})/);

    if (dateMatch) {
      return {
        ...file,
        startDate: dateMatch[1],
        endDate: dateMatch[2],
      };
    }

    // In case of no match, return original object with null dates
    return {
      ...file,
      startDate: null,
      endDate: null,
    };
  }) as DownloadableContent[];

  return modifiedArray;
};

//* Background color of status filed
export const getDownloadableStatusBackground = (downloadableData: DownloadableContent): string => {
  const { exportStatus }: { exportStatus: number } = downloadableData;

  const statusColor: { [key: number | string]: string } = {
    "-1": statusRed,
    1: infoColor, //
    2: customBgColor, // lightYellow
    3: statusGreen,
  };

  const bgcColor: string = statusColor[exportStatus] || ""; // Use the map, default to ""
  return bgcColor;
};

//* Calculate 30 days before today
export const thirtyDaysBefore = (): Date => {
  const today: Date = new Date();
  const thirtyDaysDateToSet: Date = new Date(today);
  thirtyDaysDateToSet.setDate(today.getDate() - 29);
  return thirtyDaysDateToSet;
};

//* Calculate number of days
export const calculateDaysNumber = (date1: string, date2: string): number => {
  if (!date1 || !date2) return 0;

  const oneDay: number = 24 * 60 * 60 * 1000; // hours * minutes * seconds * milliseconds

  const startDate: Date = new Date(date1); // Starting date
  const endDate: Date = new Date(date2); // Ending date
  const diffInTime: number = endDate.getTime() - startDate.getTime(); // Difference in milliseconds
  const daysNumber: number = Math.abs(Math.round(diffInTime / oneDay));
  return daysNumber + 1;
};

//* Speed Unit
export const getSpeedWithUnit = (speed: number | string, speedUnit: string): string => {
  if (!speed || !speedUnit) {
    return i18n.t("b/d");
  }

  const normalizedSpeed: number = Math.round(Number(speed));

  const speedWithUnitToReturn: string =
    speedUnit === "kmh" ? `${normalizedSpeed} km/h` : speedUnit === "mph" ? `${normalizedSpeed} mph` : i18n.t("b/d");
  return speedWithUnitToReturn;
};

//* Calculate max extents
export const calculateMaxExtents = (stops: StopInfo[], dist: number): Extent => {
  const stopsLat = stops?.map((stop: StopInfo) => stop?.lat) as number[];
  const stopsLon = stops?.map((stop: StopInfo) => stop?.lon) as number[];
  const [minLat, maxLat] = [Math.min(...stopsLat) - dist, Math.max(...stopsLat) + dist];
  const [minLon, maxLon] = [Math.min(...stopsLon) - dist, Math.max(...stopsLon) + dist];

  const extents: Extent = {
    minLat,
    maxLat,
    minLon,
    maxLon,
  };
  return extents;
};

export const filterBusesByKindOfTimeDeviation = (busesTogether: BusInfo[], kindOfTimeDeviation: string): BusInfo[] => {
  //* Filter buses by kindOfTimeDeviation
  const filteredBusesTogether: BusInfo[] =
    kindOfTimeDeviation === "workingBuses"
      ? [...busesTogether]
      : kindOfTimeDeviation === "delayedBuses"
        ? busesTogether.filter((bus: BusInfo) => bus?.addedStatus === "delayedBus")
        : kindOfTimeDeviation === "busesOnTime"
          ? busesTogether.filter((bus: BusInfo) => bus?.addedStatus === "busOnTime")
          : kindOfTimeDeviation === "busesTooFast"
            ? busesTogether.filter((bus: BusInfo) => bus?.addedStatus === "busTooFast")
            : kindOfTimeDeviation === "busesWithLayover"
              ? busesTogether.filter((bus: BusInfo) => bus?.addedStatus === "layoverBus")
              : [];
  // console.log("filteredBusesTogether:", filteredBusesTogether);

  return filteredBusesTogether;
};

//* Colors utilities -> TableBigComponentMap/ HeadwayPerformanceMap
export const routeColorForTableComponent = (routesInfo: RouteDetails[], activeBusData: ActiveBusData): string => {
  return (
    routesInfo?.filter((route: RouteDetails) => route?.shortName === activeBusData?.tableRouteData?.routeId)?.[0]?.color ||
    routesInfo?.filter((route: RouteDetails) => route?.id === activeBusData?.tableRouteData?.routeId)?.[0]?.color ||
    primaryColor
  );
};

export const sortNumericallyAndAlphabetically = (arr: string[]): string[] => {
  return arr.sort((a: string, b: string) => {
    const numA: number = parseFloat(a);
    const numB: number = parseFloat(b);
    const aIsNum: boolean = !isNaN(numA);
    const bIsNum: boolean = !isNaN(numB);

    if (aIsNum && bIsNum) {
      return numA - numB;
    } else if (aIsNum) {
      return -1; // Numbers come before strings
    } else if (bIsNum) {
      return 1; // Numbers come before strings
    } else {
      return a.localeCompare(b);
    }
  });
};

//* Date to Unix Epoch Time (1 millisecond after midnight)
export const getEpochStartTime = (date: Date, offsetMinutes: number): number => {
  const dateStr: string = date.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "numeric",
  });

  // 1. Parse the DD.MM.YYYY format
  const [day, month, year] = dateStr.split(".").map(Number);

  // 2. Create a UTC Date object set to 0:0:1 for that day Note: Month is 0-indexed in JavaScript Date (January is 0)
  const utcDate: Date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 1));

  // 3. Adjust the time based on the offset.
  // JavaScript's native offset is positive if the local timezone is behind UTC, and negative if it is ahead. We add the minutes converted to milliseconds.
  const targetTimestampStart: number = utcDate.getTime() + offsetMinutes * 60 * 1000;
  // console.log("targetTimestampStart:", targetTimestampStart);
  return targetTimestampStart;
};

//* Date to Unix Epoch Time (1 millisecond before midnight)
export const getEpochEndTime = (date: Date, offsetMinutes: number): number => {
  const dateStr: string = date.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "numeric",
  });

  // 1. Parse the DD.MM.YYYY format
  const [day, month, year] = dateStr.split(".").map(Number);

  // 2. Create a UTC Date object set to 23:59:59 for that day Note: Month is 0-indexed in JavaScript Date (January is 0)
  const utcDate: Date = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));

  // 3. Adjust the time based on the offset.
  // JavaScript's native offset is positive if the local timezone is behind UTC, and negative if it is ahead. We add the minutes converted to milliseconds.
  const targetTimestampEnd: number = utcDate.getTime() + offsetMinutes * 60 * 1000;
  // console.log("targetTimestampEnd:", targetTimestampEnd);
  return targetTimestampEnd;
};

//* Convert 20250630 -> 2025.06.30
export const convertToDateFormat = (number: string | number | undefined): string => {
  if (!number) return "";

  const year: string = number.toString().slice(0, 4);
  const month: string = number.toString().slice(4, 6);
  const day: string = number.toString().slice(6);

  return `${year}.${month}.${day}`;
};

export const daysUntil = (dateStr: string): number => {
  // Parse "2026.08.31" -> [2026, 8, 31], note month is 0-indexed
  const [year, month, day] = dateStr.split(".").map(Number);
  const targetDate: Date = new Date(year, month - 1, day);
  const now: Date = new Date();

  // Milliseconds in a day
  const oneDay: number = 1000 * 60 * 60 * 24;

  // Difference in milliseconds, ensure positive
  const diffTime: number = Math.max(0, targetDate.getTime() - now.getTime());

  // Convert to whole days
  return Math.floor(diffTime / oneDay);
};

//* Sort numerically or alphabetically array of objects by idKey
export const sortNumericallyAndAlphabeticallyArrayOfObjects = <T extends { id: string | number }>(
  arr: T[],
  idKey: keyof T
): T[] => {
  return [...arr].sort((a: T, b: T) => {
    const numA: number = parseFloat(a[idKey] as string);
    const numB: number = parseFloat(b[idKey] as string);
    const aIsNum: boolean = !isNaN(numA);
    const bIsNum: boolean = !isNaN(numB);

    if (aIsNum && bIsNum) {
      return numA - numB;
    } else if (aIsNum) {
      return -1; // Numbers come before strings
    } else if (bIsNum) {
      return 1; // Numbers come before strings
    } else {
      return String(a[idKey]).localeCompare(String(b[idKey]));
    }
  });
};

//* Bus filling color
export const getBusFillingColor = (percentage: string | number): string => {
  // console.log("percentage:", percentage);
  if (isNaN(Number(percentage)) || percentage === null || percentage === undefined || percentage === -1) return "";

  // Ensure the percentage stays within 0-100 bounds
  const clampedPercentage = Math.max(0, Math.min(100, Number(percentage)));

  // Map 0-100 to a hue range of 120 (green) down to 0 (red)
  // Formula: Hue = 120 - (1.2 * percentage)
  const hue: number = 120 - clampedPercentage * 1.2;

  // We keep saturation at 80% and lightness at 50% for vibrant colors
  return `hsl(${hue}, 80%, 50%)`;
};

export const getAverageDeviationBackgroundFormat = (
  time: string | null
): {
  background: string;
  value: string;
} => {
  if (!time) {
    return {
      background: secondaryColor as string,
      value: i18n.t("b/d"),
    };
  }

  // 1. Check if the string starts with a minus sign
  const isNegative: boolean = time?.startsWith("-");
  // console.log("isNegative:", isNegative);

  // 2. Remove the minus sign if present so we can parse the numbers
  const cleanTime: string = isNegative ? time.slice(1) : time;

  const [minutes, seconds] = cleanTime.split(":").map(Number);
  // console.log("minutes, seconds:", minutes, seconds);

  if (isNaN(minutes) || isNaN(seconds)) {
    console.log("Invalid time format. Expected mm:ss or -mm:ss");
  }

  // 3. Calculate absolute milliseconds
  const absMs: number = minutes * 60 * 1000 + seconds * 1000;

  // 4. Final negative or positive result
  const finalValue: number = isNegative ? -absMs : absMs;
  // console.log("finalValue:", finalValue);

  const finalStr: string = toMinutesAndSeconds(finalValue);
  // console.log("finalStr:", finalStr);

  const finalBgc: string = showColorStatus(finalValue);
  // console.log("finalBgc:", finalBgc);

  // 5. Return
  return {
    background: finalBgc,
    value: finalStr,
  };
};

//* Check if value is numeric
export const isNumeric = (value: any): boolean => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

//* Normalize timestamp
export const normalizeTimestamp = (timestamp: number): number => {
  if (!timestamp) {
    return 0;
  }

  const length: number = timestamp.toString().length;

  if (length === 10) {
    return timestamp * 1000;
  } else if (length === 13) {
    return timestamp;
  }

  return 0;
};
